import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { AssetCategorySchema, RatingUnitSchema } from './categories.js';
import { AssetEventReasonTypeSchema, AssetEventTypeSchema } from './events.js';
import { AccessPathSchema, DEVICE_ACCESS_KINDS, ModelConnectivitySchema, SimSlotSchema } from './access.js';
import { AssetStatusSchema } from './requests.js';
import { paginatedResponseSchema } from '../common/pagination.js';
import { BandKeySchema, BandSchema } from '../common/bands.js';
// Register response shapes. Field-exposure rule (ANATOMY L7): the ops view is
// the minimum the register list/detail needs; BOOKS and ACCESS are separate
// gated endpoints (different audience = different endpoint), never extra
// fields here.
const ref = z.object({ id: z.string(), label: z.string() }).strict();
// Where a thing sits, resolved for display: label derives from the host for
// derived warehouse kinds (site storage → site name, vehicle stock → plate —
// ADR-066: derived rows have no identity of their own).
export const AssetLocationRefSchema = z
    .object({
    kind: z.enum(['site', 'depot', 'site_storage', 'vehicle_stock']),
    site_id: z.string().nullable(), // the site (kind site) or the storage's host site
    warehouse_id: z.string().nullable(), // set for the three warehouse kinds
    label: z.string(),
    reference: z.string().nullable(), // the place's code: the site's reference · a depot's reference · a vehicle hold's plate (copiable in lists/group headers); null for a site storage (its host's reference speaks)
    timezone_iana: z.string().nullable(), // the site's zone (kind site · a site storage's host) — the asset's own screens follow the site it stands at (ADR-082 §2); null in a depot or a vehicle hold (the project's zone then)
    can_open: z.boolean(), // may THIS viewer open the site (kind site · a site storage's host) — the site read's rule: the sites key, the site in reach (a unit's own place is in reach by construction; a History place is also this project's own site); false off a site — a reference never opens onto a refusal (ADR-036 §6)
})
    .strict();
// A place or a unit the viewer may not see, named by its KIND alone (ADR-066
// §9) — "a depot", "a site", "a unit": the name and the id never travel.
export const AssetHiddenRefSchema = z
    .object({
    hidden: z.literal(true),
    kind: z.enum(['site', 'depot', 'site_storage', 'vehicle_stock', 'unit']),
})
    .strict();
/** A related place as the viewer may name it. */
const placeOrHidden = z.union([AssetLocationRefSchema, AssetHiddenRefSchema]);
/** A related unit / site as the viewer may name it. */
const refOrHidden = z.union([ref, AssetHiddenRefSchema]);
export const AssetModelRefSchema = z
    .object({
    id: z.string(),
    label: z.string(), // the model name
    manufacturer: z.string().nullable(),
    is_generic: z.boolean(),
    is_canonical: z.boolean(),
    is_disabled: z.boolean(), // the catalogue no longer offers it — an edit form shows it marked, never re-picked
})
    .strict();
// ── Devices-entry row block (face=devices — the connectivity RECORD at list
// grain; ADR-063 §access facet). No secrets and no per-field markers at this grain —
// `credential_state` is ONE derived word (the per-field markers stay on the
// facet read), so a projection bug cannot leak. `address` is the row's own
// device_ip: the dial address on Direct rows, the local/LAN address on Via
// rows (the dial story is the path + the facet).
export const AssetRowConnectivitySchema = z
    .object({
    path: AccessPathSchema,
    via: ref.nullable(), // the fronting unit (product-first label)
    address: z.string().nullable(),
    // Declared entries only (registry order) — capability ghosts never ride
    // the list (the ruled column charter — the identity cell already names the product).
    kinds: z.array(z.enum(DEVICE_ACCESS_KINDS)),
    // Deployed units reached through THIS one (the gear-off Fronts column).
    fronts_count: z.number().int(),
    // complete = every declared secret-bearing kind holds its primary secret ·
    // missing = at least one primary absent · none_required = only secretless
    // kinds declared (or none). Status-BLIND by design (the Devices-entry filter ruling (2026-08-31)): the
    // filter must find a stocked secretless router before it deploys.
    credential_state: z.enum(['complete', 'missing', 'none_required']),
})
    .strict();
export const AssetListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.string(),
    category: AssetCategorySchema,
    status: AssetStatusSchema,
    model: AssetModelRefSchema.nullable(),
    serial_number: z.string().nullable(),
    name: z.string().nullable(),
    reference: z.string(), // MANDATORY (2026-08-09 ruling — NOT NULL at genesis)
    reference_alt: z.string().nullable(),
    unit_count: z.number().int().nullable(),
    rating_value: z.number().nullable(),
    rating_unit: RatingUnitSchema.nullable(),
    location: AssetLocationRefSchema.nullable(),
    parent_asset_id: z.string().nullable(),
    is_unallocated: z.boolean(), // current_project_id IS NULL (visible at attached depots)
    // The health verdict cache (4-grade; poor DISPLAYS "Degraded" — ruled) +
    // warranty end — ops-visible row facts (batch-2: register columns, off by
    // default), never books-gated.
    condition: z.enum(['good', 'fair', 'poor', 'failed']).nullable(),
    condition_on: z.string().nullable(),
    warranty_end_on: z.string().nullable(),
    // The status clock — the day of the latest status change, stamped by the
    // moment engine on EVERY transition — + the SOH evidence beside the grade
    // (the story's merged Condition column).
    status_on: z.string().nullable(),
    location_on: z.string().nullable(), // the location clock (cache of events — the peek shows it when it differs)
    state_of_health_pct: z.number().nullable(),
    // The stocked earmark, on the ROW (the story's location-line marker).
    reserved_for: refOrHidden.nullable(),
    // DERIVED display rating (ADR-065 ladder): catalog model's per-unit spec ×
    // unit count, or the generic row's own pair. Never stored.
    rating: z.object({ value: z.number(), unit: RatingUnitSchema }).strict().nullable(),
    // The row's raised observation FAMILIES, server-derived at read time
    // (ADR-064 "read-time columns"): the Observations cell + the family filter
    // and the strip's kind counts all speak this one derivation. Each face
    // carries ITS OWN families (the three-cockpits ruling): register = the
    // physical record's · devices = the connectivity record's · books = the
    // money record's; the detail read keeps viewer-scoped completeness.
    observations: z.array(z.string()),
    // Books-face columns (face=books only — the route gates asset.books:read;
    // the ops face never carries finance fields per the L7 exposure rule).
    books: z
        .object({
        owner: z.object({ id: z.string(), name: z.string() }).nullable(),
        acquired_on: z.string().nullable(),
        acquisition_cost: z.number().nullable(),
        acquisition_currency: z.string().nullable(),
        acquisition_mode: z.enum(['purchased', 'leased', 'customer_provided', 'other']).nullable(),
        acquired_condition: z.enum(['new', 'used']).nullable(),
        supplier: z.string().nullable(),
        disposal_method: z.enum(['scrapped', 'sold', 'donated', 'returned']).nullable(),
        written_off_on: z.string().nullable(),
        disposed_on: z.string().nullable(),
        // The depreciation-start day (first entry into service, stamp-once) —
        // the Book-status cell's date line for on-books rows.
        first_activated_on: z.string().nullable(),
        book_status: z.enum(['on_books', 'written_off', 'disposed']),
    })
        .strict()
        .nullable()
        .default(null),
    // The Devices-entry block (face=devices only — the books-face precedent;
    // the register/books faces never haul connectivity).
    connectivity: AssetRowConnectivitySchema.nullable().default(null),
    // The row's band in a grouped answer — the key of the band it sits in (the expression that counted the bands;
    // the screen computes none). Absent on an ungrouped read.
    band: BandKeySchema.optional(),
    created_at: z.string(),
})
    .strict();
// The strip's truthful counts (the sites grammar), all in ONE pass over the filtered register (ADR-078 §2): status
// partitions (sums to the population); kinds overlap (a row can raise several — may sum past it). A chip's number
// respects every OTHER axis's selection and exempts its own — it holds still when pressed.
export const AssetStatusCountsSchema = z
    .object({ stocked: z.number(), deployed: z.number(), on_hold: z.number(), retired: z.number(), lost: z.number() })
    .strict();
export const AssetKindCountsSchema = z
    .object({ missing: z.number(), conflict: z.number(), unverified: z.number() })
    .strict();
export const AssetListResponseSchema = paginatedResponseSchema(AssetListItemSchema).extend({
    status_counts: AssetStatusCountsSchema,
    kind_counts: AssetKindCountsSchema,
    // Per-FAMILY counts for the Devices entry's chip strip (face=devices only;
    // null elsewhere). Keyed by full family key so the additive sheet grows
    // without a wire change; computed sans the family lens (a chip's number
    // holds still while you press it — the strip law).
    family_counts: z.record(z.string(), z.number()).nullable().default(null),
    // A grouped answer (`group_by` — ADR-078 §4): every band of the filtered register as {key, label, count} in the
    // axis's own order — `items` is then the STREAM (the opened bands' rows, each stamped `band`) and `total` the
    // opened bands' counts summed. Absent when the read was not grouped.
    bands: z.array(BandSchema).optional(),
});
export const AssetChildRefSchema = z
    .object({
    id: z.string(),
    category: AssetCategorySchema,
    status: AssetStatusSchema,
    label: z.string(), // name ?? serial ?? category key (display fallback chain)
})
    .strict();
export const AssetDetailSchema = AssetListItemSchema.extend({
    is_complete: z.boolean(), // the register face's families raise nothing — the same for every viewer (never a gate — ADR-063)
    parent: ref.nullable(),
    children: z.array(AssetChildRefSchema),
    reserved_for_site: refOrHidden.nullable(),
    state_of_health_pct: z.number().nullable(),
    state_of_health_on: z.string().nullable(),
    last_seen_at: z.string().nullable(), // census stamp (the reconciliation engine writes it — ADR-064; read-only here)
    deployed_on: z.string().nullable(),
    first_activated_on: z.string().nullable(), // FIRST entry into service, ever (stamp-once; deployed_on re-stamps per return)
    retired_on: z.string().nullable(),
    lost_on: z.string().nullable(),
    network_capable: z.boolean(), // is_network_device ?? category default — the access-facet affordance
    // Ops-visible half of the dual-homed supplier/warranty pair (the 2026-08-09
    // ruling): field operations generates + consumes both (claims, RMA), so the
    // record editor reads them without the books gate. Warranty already rides
    // the list item; supplier joins here. Cost/owner/mode stay books-gated.
    supplier: z.string().nullable(),
}).strict();
// ── Books facet (asset.books:read) ──────────────────────────────────────────
export const AssetBooksSchema = z
    .object({
    row_version: RowVersionSchema,
    owner: z.object({ id: z.string(), name: z.string(), code: z.string().nullable() }).nullable(),
    acquired_on: z.string().nullable(),
    acquired_condition: z.enum(['new', 'used']).nullable(),
    acquisition_cost: z.number().nullable(),
    acquisition_currency: z.string().nullable(),
    acquisition_mode: z.enum(['purchased', 'leased', 'customer_provided', 'other']).nullable(),
    supplier: z.string().nullable(),
    warranty_end_on: z.string().nullable(),
    first_activated_on: z.string().nullable(), // depreciation start (first entry into service, stamp-once)
    written_off_on: z.string().nullable(),
    disposed_on: z.string().nullable(),
    disposal_method: z.enum(['scrapped', 'sold', 'donated', 'returned']).nullable(),
    book_status: z.enum(['on_books', 'written_off', 'disposed']), // DERIVED (ADR-061 posture)
})
    .strict();
// ── Access facet (the ADDRESS half — asset:read; the reveal alone is
// asset.private_fields:read) ─────────────────────────────────────────────────
// Secrets are WRITE-ONLY on the wire: `fields` carries only non-secret values;
// `secrets_set` names which secret fields currently hold a sealed value
// (derived from DEVICE_ACCESS_SECRET_FIELDS — a projection bug cannot leak).
export const DeviceAccessViewEntrySchema = z
    .object({
    kind: z.enum(DEVICE_ACCESS_KINDS),
    fields: z.record(z.string(), z.union([z.string(), z.number()])),
    secrets_set: z.array(z.string()),
    // The cellular entry's SIM slot records (ADR-063 rev — zero secrets, so
    // the whole record passes through the view). Absent on every other kind.
    sims: z.array(SimSlotSchema).optional(),
})
    .strict();
export const AssetAccessSchema = z
    .object({
    row_version: RowVersionSchema,
    network_capable: z.boolean(),
    device_ip: z.string().nullable(),
    // The derived path (ADR-063's addressing law): via when an edge is set,
    // direct when the IP alone is, none otherwise — never a stored mode.
    access_path: AccessPathSchema,
    // The fronting device this unit is reached through (label = the product-
    // first asset label), and the units reached through THIS one.
    accessed_via: ref.nullable(),
    fronts: z.array(ref),
    // The PRODUCT's connectivity (the four-layer law): capabilities · SIM
    // slots · can-front — the unit inherits the service list and carries only
    // its own facts (address · ports · credentials · populated SIMs). null =
    // a generic unit (no model to inherit from).
    model: ModelConnectivitySchema.nullable(),
    entries: z.array(DeviceAccessViewEntrySchema),
})
    .strict();
// The audited per-field reveal — one sealed value per call, never a bulk door.
export const RevealDeviceSecretBodySchema = z
    .object({ kind: z.enum(DEVICE_ACCESS_KINDS), field: z.string().trim().min(1).max(32) })
    .strict();
export const RevealDeviceSecretResponseSchema = z.object({ value: z.string() }).strict();
// ── History (the lifecycle timeline — asset_events; the audit-trail render is
// the audit module's read) ──────────────────────────────────────────────────
export const AssetEventItemSchema = z
    .object({
    id: z.number().int(),
    event_type: AssetEventTypeSchema,
    effective_on: z.string(),
    recorded_at: z.string(),
    project_id: z.string(),
    actor_user: ref.nullable(),
    actor_api_client: ref.nullable(),
    from: placeOrHidden.nullable(),
    to: placeOrHidden.nullable(),
    // The status + condition axis pairs (uniform-axes law: both ends iff the
    // axis changed) — the EventCard renders one line per changed axis.
    from_status: AssetStatusSchema.nullable(),
    to_status: AssetStatusSchema.nullable(),
    from_condition: z.enum(['good', 'fair', 'poor', 'failed']).nullable(),
    to_condition: z.enum(['good', 'fair', 'poor', 'failed']).nullable(),
    unit_count_delta: z.number().int().nullable(),
    counterpart: refOrHidden.nullable(),
    from_owner: ref.nullable(),
    to_owner: ref.nullable(),
    reason_type: AssetEventReasonTypeSchema.nullable(),
    note: z.string().nullable(),
    correlation_id: z.string(),
})
    .strict();
// Pagination "none" v1: a unit's lifecycle is tens of events (recorded in the
// PR; cursor arrives when a consumer shows unbounded growth).
export const AssetHistoryResponseSchema = z.object({ items: z.array(AssetEventItemSchema) }).strict();
// Filter options — the '+ Filter' menus' option lists (models used in this
// project's register; owners for the books face). One heavy read (the lane
// gate's heavy set), cached client-side.
export const AssetFilterOptionsSchema = z
    .object({
    // Each product with its category — the Product filter narrows to the chosen Category (the rows' parent mark).
    models: z.array(z.object({ id: z.string(), label: z.string(), category: AssetCategorySchema }).strict()),
    // is_disabled: the filter keeps every attached owner (old rows stay
    // findable); the books pickers offer only enabled ones + the current value.
    owners: z.array(z.object({ id: z.string(), name: z.string(), is_disabled: z.boolean() }).strict()),
})
    .strict();
export const AssetIdResponseSchema = z.object({ id: z.string() }).strict();
export const ReplaceResultResponseSchema = z
    .object({ outgoing_id: z.string(), incoming_id: z.string() })
    .strict();
